import { Button } from "@legal-500/legal500-design-system";
import { useEffect, useId, useRef, useState } from "react";
import {
  pendingCompaniesService,
  type CompanyReviewPreview,
  type PendingCompany,
} from "../../../api/pendingCompanies.service";
import { masterCompaniesService, type WebsiteCompany } from "../../../api/masterCompanies.service";
import type { ReviewAction } from "./ReviewPanel";
import styles from "./Review.module.css";

export default function ReviewForm({
  record,
  action,
  onBusy,
  onCancel,
  onSuccess,
  onRefresh,
  inline = false,
}: {
  inline?: boolean;
  record: PendingCompany;
  action: ReviewAction;
  onBusy: (busy: boolean) => void;
  onCancel: () => void;
  onSuccess: () => void;
  onRefresh: () => void;
}) {
  const [snapshot, setSnapshot] = useState(record);
  const [name, setName] = useState(snapshot.name);
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState(record.status === "pending" ? "create" : "existing");
  const [selected, setSelected] = useState<WebsiteCompany | null>(null);
  const [preview, setPreview] = useState<CompanyReviewPreview | null>(null);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [displayedRevision, setDisplayedRevision] = useState(snapshot.revision);
  const [latestName, setLatestName] = useState<string | null>(null);
  const [nameConfirmed, setNameConfirmed] = useState(true);
  const nameInput = useRef<HTMLInputElement>(null);
  const latestNameId = useId();
  const latestNameDiffers = latestName !== null && latestName !== name.trim();
  const awaitingNameConfirmation = latestNameDiffers && !nameConfirmed;
  const inFlight = useRef(false);
  const initialStatus = useRef(record.status);
  const initialCompanyId = useRef(record.companyId);
  const companyChanged = action === "correct" && snapshot.companyId !== initialCompanyId.current;
  const form = useRef<HTMLFormElement>(null);
  const existingOnly = action === "relink" || record.status === "rejected";
  useEffect(() => {
    form.current?.querySelector<HTMLElement>("input:not(:disabled), select, textarea")?.focus();
  }, []);
  useEffect(() => {
    if (action !== "correct") return;
    setPreview(null);
    if (!snapshot.companyId || snapshot.companyId !== initialCompanyId.current) return;
    let active = true;
    pendingCompaniesService
      .preview(snapshot.companyId)
      .then(result => {
        if (active) {
          setPreview(result.data);
          setName(result.data.name);
        }
      })
      .catch(caught => {
        if (active)
          setError(caught instanceof Error ? caught.message : "Unable to load company impact.");
      });
    return () => {
      active = false;
    };
  }, [action, snapshot.companyId]);
  useEffect(() => {
    if (action === "correct" && preview)
      form.current?.querySelector<HTMLInputElement>("input")?.focus();
  }, [action, preview]);
  const decision = action === "approve" || action === "reject" || action === "relink";
  const selectedAlreadyLinked =
    action === "relink" && selected !== null && selected.id === snapshot.companyId;
  const valid =
    !busy &&
    !refreshing &&
    !conflict &&
    !awaitingNameConfirmation &&
    !selectedAlreadyLinked &&
    reason.trim().length > 0 &&
    reason.trim().length <= 1000 &&
    (decision || (name.trim().length > 0 && name.trim().length <= 255)) &&
    ((action !== "approve" && action !== "relink") ||
      (!existingOnly && mode === "create") ||
      selected !== null) &&
    (action !== "correct" || (!companyChanged && preview?.id === initialCompanyId.current)) &&
    (action === "correct" || action === "relink"
      ? snapshot.status === "approved"
      : action === "approve"
        ? snapshot.status === initialStatus.current && snapshot.status !== "approved"
        : snapshot.status === "pending");
  async function refreshConflict() {
    setRefreshing(true);
    const previousName = action === "correct" ? preview?.name : snapshot.name;
    try {
      const latest = await pendingCompaniesService.detail(snapshot.id);
      let refreshedName = latest.data.name;
      if (action === "correct") {
        setPreview(null);
        if (latest.data.companyId !== initialCompanyId.current) {
          setSnapshot(latest.data);
          setConflict(false);
          setError(
            "The linked company changed. Cancel this edit and reopen it to confirm the current company before saving.",
          );
          onRefresh();
          return;
        }
        if (initialCompanyId.current) {
          const refreshed = (await pendingCompaniesService.preview(initialCompanyId.current)).data;
          setPreview(refreshed);
          refreshedName = refreshed.name;
        }
      }
      setSnapshot(latest.data);
      setDisplayedRevision(latest.data.revision);
      if (selected)
        setSelected({ ...selected, ...(await pendingCompaniesService.preview(selected.id)).data });
      setConflict(false);
      const nameOverwritten =
        (action === "edit" || action === "correct") &&
        previousName !== undefined &&
        refreshedName !== previousName &&
        refreshedName !== name.trim();
      if (nameOverwritten) {
        setLatestName(refreshedName);
        setNameConfirmed(false);
        setError(
          `Another staff member changed this name to “${refreshedName}”. Keep your name or edit it before saving.`,
        );
      } else setError("");
      onRefresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to refresh review.");
    } finally {
      setRefreshing(false);
    }
  }
  async function submit() {
    if (!valid || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      if (action === "edit")
        await pendingCompaniesService.edit(
          snapshot.id,
          name.trim(),
          reason.trim(),
          displayedRevision,
        );
      else if (action === "approve" || action === "relink")
        await pendingCompaniesService.approve(
          snapshot.id,
          displayedRevision,
          reason.trim(),
          mode === "existing" ? selected?.id : undefined,
          mode === "existing" ? selected?.revision : undefined,
        );
      else if (action === "reject")
        await pendingCompaniesService.reject(snapshot.id, displayedRevision, reason.trim());
      else if (initialCompanyId.current && preview)
        await pendingCompaniesService.correct(
          initialCompanyId.current,
          name.trim(),
          reason.trim(),
          preview.revision,
          { submissionId: snapshot.id, submissionRevision: displayedRevision },
        );
      onSuccess();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to save review.");
      if (caught instanceof Error && "status" in caught && caught.status === 409) setConflict(true);
    } finally {
      inFlight.current = false;
      setBusy(false);
      onBusy(false);
    }
  }
  return (
    <form
      ref={form}
      className={inline ? styles.inlineForm : styles.form}
      onSubmit={event => {
        event.preventDefault();
        void submit();
      }}
    >
      {!inline && (
        <h3>
          {
            {
              edit: "Edit name",
              approve: "Approve entry",
              reject: "Reject entry",
              correct: "Edit shared company name",
              relink: "Change linked company",
            }[action]
          }
        </h3>
      )}
      {action === "correct" && (
        <p>
          {preview
            ? `This changes the shared company “${preview.name}” and ${preview.affectedProfiles} currently linked profiles. Personal employer choices remain linked to the same company.`
            : companyChanged
              ? "Company change detected. A new shared-name confirmation is required."
              : "Loading affected profile count…"}
        </p>
      )}
      {!decision && latestNameDiffers && (
        <div>
          <p id={latestNameId}>
            Current name: <strong>{latestName}</strong>
          </p>
          {awaitingNameConfirmation && (
            <Button
              color="primary"
              className={styles.brandButton}
              variant="outlined"
              size="small"
              disabled={busy}
              onClick={() => {
                setNameConfirmed(true);
                setError("");
                nameInput.current?.focus();
              }}
            >
              Keep my name
            </Button>
          )}
        </div>
      )}
      {!decision && (
        <label className={styles.field}>
          <span className={inline ? styles.visuallyHidden : undefined}>Company name</span>
          <input
            ref={nameInput}
            value={name}
            onChange={event => {
              setName(event.target.value);
              if (awaitingNameConfirmation) {
                setNameConfirmed(true);
                setError("");
              }
            }}
            maxLength={255}
            required
            aria-describedby={latestNameDiffers ? latestNameId : undefined}
            disabled={busy || (action === "correct" && !preview)}
          />
        </label>
      )}
      {(action === "approve" || action === "relink") && (
        <>
          {!existingOnly && (
            <label className={styles.field}>
              Approval target
              <select
                value={mode}
                onChange={event => {
                  setMode(event.target.value);
                  setSelected(null);
                }}
                disabled={busy}
              >
                <option value="create">Create a new company</option>
                <option value="existing">Link an existing company</option>
              </select>
            </label>
          )}
          {existingOnly && (
            <p>
              Select an existing company. No new company will be created. Previous decisions remain
              in history.
            </p>
          )}
          {mode === "existing" && <CataloguePicker disabled={busy} onSelect={setSelected} />}
          <p>
            {action === "relink" ? "Link to:" : "Approve as:"}{" "}
            <strong>
              {mode === "create" ? snapshot.name : (selected?.name ?? "Select an existing company")}
            </strong>
          </p>
          {selected && <p>ID: {selected.id}</p>}
          {selectedAlreadyLinked && (
            <p role="status">
              This entry is already linked to this company. Choose a different company.
            </p>
          )}
        </>
      )}
      <label className={`${styles.field} ${styles.reasonField}`}>
        Staff reason
        <textarea
          value={reason}
          onChange={event => setReason(event.target.value)}
          maxLength={1000}
          required
          disabled={busy || (action === "correct" && !preview)}
          aria-describedby="company-reason-help"
        />
      </label>
      <p id="company-reason-help">
        Give a reason for the audit history. Do not include personal information.
      </p>
      {snapshot.status !== initialStatus.current && (
        <p role="alert">
          This entry is now {snapshot.status}. Cancel this action to review its current state.
        </p>
      )}
      {error && <p role="alert">{error}</p>}
      {conflict && (
        <div>
          <p>
            The record changed or conflicts with a company. Refresh the review before retrying. You
            can choose an existing company when approving.
          </p>
          <Button
            color="primary"
            className={styles.brandButton}
            variant="outlined"
            disabled={refreshing}
            onClick={() => void refreshConflict()}
          >
            Refresh before retrying
          </Button>
        </div>
      )}
      <div className={inline ? styles.actions : styles.footer}>
        <Button
          color="primary"
          className={styles.brandButton}
          variant="outlined"
          disabled={busy}
          onClick={onCancel}
        >
          {inline ? "Cancel" : "Cancel action"}
        </Button>
        <Button
          color={action === "approve" ? "success" : action === "reject" ? "error" : "primary"}
          className={
            action === "approve"
              ? styles.approveAction
              : action === "reject"
                ? styles.rejectAction
                : styles.brandButton
          }
          variant="contained"
          type="submit"
          disabled={!valid}
        >
          {busy
            ? "Saving…"
            : action === "relink"
              ? "Confirm company change"
              : action === "approve"
                ? "Confirm approval"
                : action === "reject"
                  ? "Confirm rejection"
                  : inline
                    ? "Save"
                    : "Save name"}
        </Button>
      </div>
    </form>
  );
}

const CATALOGUE_SEARCH_DEBOUNCE_MS = 300;
const CATALOGUE_SEARCH_LIMIT = 20;

function CataloguePicker({
  disabled,
  onSelect,
}: {
  disabled: boolean;
  onSelect: (company: WebsiteCompany | null) => void;
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<WebsiteCompany | null>(null);
  const [rows, setRows] = useState<WebsiteCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = useId();
  const container = useRef<HTMLDivElement>(null);
  const term = search.trim();
  const showList = open && term.length > 0;
  const listVisible = showList && rows.length > 0;
  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);
  useEffect(() => {
    if (selected && search === selected.name) return;
    if (term.length === 0) {
      setRows([]);
      setLoading(false);
      setError("");
      return;
    }
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      masterCompaniesService
        .list(term, CATALOGUE_SEARCH_LIMIT, 0, "name", "asc")
        .then(result => {
          if (!active) return;
          setRows(result.data);
          setActiveIndex(-1);
          setError("");
        })
        .catch(caught => {
          if (!active) return;
          setRows([]);
          setError(caught instanceof Error ? caught.message : "Unable to search companies.");
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, CATALOGUE_SEARCH_DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [search, selected, term]);
  const optionId = (index: number) => `${listId}-option-${index}`;
  const choose = (company: WebsiteCompany) => {
    setSelected(company);
    setSearch(company.name);
    setOpen(false);
    setActiveIndex(-1);
    onSelect(company);
  };
  return (
    <fieldset disabled={disabled} className={styles.form}>
      <legend>Find an existing company</legend>
      <div ref={container} className={styles.picker}>
        <label className={styles.field}>
          Search existing companies
          <input
            role="combobox"
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={listVisible}
            aria-controls={listVisible ? listId : undefined}
            aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
            value={search}
            onFocus={() => setOpen(true)}
            onChange={event => {
              setSearch(event.target.value);
              setOpen(true);
              setActiveIndex(-1);
              if (selected) {
                setSelected(null);
                onSelect(null);
              }
            }}
            onKeyDown={event => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                if (!showList) {
                  setOpen(true);
                  return;
                }
                if (rows.length === 0) return;
                setActiveIndex(index =>
                  event.key === "ArrowDown"
                    ? (index + 1) % rows.length
                    : index <= 0
                      ? rows.length - 1
                      : index - 1,
                );
              } else if (event.key === "Enter") {
                event.preventDefault();
                const row = listVisible && activeIndex >= 0 ? rows[activeIndex] : undefined;
                if (row) choose(row);
              } else if (event.key === "Escape" && showList) {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
              }
            }}
          />
        </label>
        {listVisible && (
          <ul
            id={listId}
            role="listbox"
            aria-label="Matching companies"
            className={styles.dropdown}
          >
            {rows.map((row, index) => (
              <li
                key={row.id}
                id={optionId(index)}
                role="option"
                aria-selected={selected?.id === row.id}
                className={index === activeIndex ? styles.optionActive : styles.option}
                onMouseDown={event => event.preventDefault()}
                onClick={() => choose(row)}
              >
                {row.name} — {row.website || "No website"} <small>{row.id}</small>
              </li>
            ))}
          </ul>
        )}
      </div>
      {showList && loading && <p role="status">Searching companies…</p>}
      {showList && !loading && error && <p role="alert">{error}</p>}
      {showList && !loading && !error && rows.length === 0 && <p>No matching companies.</p>}
    </fieldset>
  );
}
